import React, { MouseEvent } from 'react';
import classnames from 'classnames';
import { HelpText } from '@mm/components';
import './Checkbox.pcss';

export type CheckBoxItem = {
  key?: string;
  isChecked: boolean;
  onToggle?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string | JSX.Element;
  className?: string;
  labelClassName?: string;
  checkboxLabelTitleClassName?: string;
  disabled?: boolean;
  name?: string;
  tooltip?: string;
  onClick?: (e?: MouseEvent<HTMLInputElement>) => any;
  onChangeInOnClick?: (isChecked: boolean) => void;
};

/**
 * Checkbox component.
 * @param isChecked sets the tick
 * @param onToggle handles toggle event. When used with formic event.target.value is set as string, but formic excpects boolean
 * @param label string label
 * @param className css class that should be applied
 * @param disabled sets checkbox disabled state
 * @param name name value
 * @param onClick click event handler. When used with formic event.target.value is set as string, but formic excpects boolean
 */

const CheckBox = ({
  isChecked,
  onToggle,
  label,
  className,
  labelClassName,
  checkboxLabelTitleClassName,
  disabled,
  name,
  onClick,
  tooltip,
  onChangeInOnClick,
}: CheckBoxItem) => {
  const handleClick = (e: MouseEvent<HTMLInputElement>) => {
    if (onClick && !disabled) {
      onClick(e);
    }
    // Note: the workaround is needed for some edge cases in how React
    // handles change event for 'checkbox' type inputs. They are initially
    // handled using the `click` event, and then React is supposed to
    // trigger `change` synthetic event, but occasinally it fails.
    // Here are some of the cases it fails in:
    // https://github.com/facebook/react/issues/13424
    // https://github.com/facebook/react/issues/8971
    // https://github.com/facebook/react/issues/13477
    // After trying for a few days to come up with a more elegant solution
    // I'm falling back to this workaround not to waste time anymore.
    if (onChangeInOnClick && !disabled) {
      onChangeInOnClick(!e.currentTarget.checked);
    }
  };
  return (
    <div
      className={classnames(className, 'checkbox', {
        'checkbox--disabled': disabled,
      })}
      title={tooltip}
    >
      <label className={classnames('checkbox-label', labelClassName)}>
        <input
          className="checkbox__input"
          onClick={handleClick}
          type="checkbox"
          checked={isChecked}
          onChange={e => {
            onToggle && onToggle(e);
          }}
          name={name}
          disabled={disabled}
        />
        {label && (
          <HelpText
            className={classnames(
              checkboxLabelTitleClassName,
              'checkbox-label__title',
            )}
            title={typeof label === 'string' ? label : ''}
            size="s"
          >
            {label}
          </HelpText>
        )}
      </label>
    </div>
  );
};

export default CheckBox;
